import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import BasicModalFormComponent from "../../../components/formComponent/BasicModalFormComponent";
import FormTextField from "../../../components/general/FormTextField";
import FormSelect from "../../../components/general/FormSelect";
import FormCheckbox from "../../../components/general/FormCheckbox";
import { postRequest } from "../../../services/mainApp.service";

const initialFormValues = {
  SubCategoryId: null,
  SubCategoryName: "",
  CategoryId: null,
  IsEnable: true,
};

const initialSearchValues = {
  SearchText: "",
};

const columns = [
  {
    title: "Sub Category Name",
    dataIndex: "SubCategoryName",
    key: "SubCategoryName",
  },
  {
    title: "Category",
    dataIndex: "CategoryName",
    key: "CategoryName",
  },
];

const InventorySubCategory = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);

  const [formFields, setFormFields] = useState(initialFormValues);
  const [searchFields, setSearchFields] = useState(initialSearchValues);
  const [itemList, setItemList] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [updateId, setUpdateId] = useState(null);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = (searchParams = {}, pageNumber = 1, pageSize = 10) => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      SubCategoryId: null,
      SearchText: searchParams.SearchText || "",
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    postRequest("/Inv_SubCategory", payload, controller)
      .then((response) => {

        setTableLoading(false);
        if (response.error === true) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error fetching data");
          return;
        }

        const dataSet = response.data;
        setItemList(dataSet.Data || []);

        // Update pagination with total count from API response
        // Assuming the API returns TotalCount or TotalRecords in the response
        const totalCount = dataSet?.TotalCount || dataSet?.TotalRecords || dataSet?.Data?.length || 0;
        setPagination((prev) => ({
          ...prev,
          current: pageNumber,
          pageSize: pageSize,
          total: totalCount,
        }));

        // Set supporting tables if available (for categories dropdown)
        if (dataSet.AdditionalData) {
          setSupportingTable({ AdditionalData: dataSet.AdditionalData });
        }
      })
      .catch((error) => {
        setTableLoading(false);
        if (error.message !== "canceled") {
          console.error("Error fetching sub categories:", error);
          message.error("Failed to fetch sub categories");
        }
      });
  };

  useEffect(() => {
    fetchData({}, pagination.current, pagination.pageSize);
    // fetchCategories();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (updateId !== null) {
      const selectedItem = itemList.find((item) => item.SubCategoryId === updateId);
      if (selectedItem) {
        setFormFields({
          SubCategoryId: selectedItem.SubCategoryId,
          SubCategoryName: selectedItem.SubCategoryName || "",
          CategoryId: selectedItem.CategoryId || null,
          IsEnable: selectedItem.IsEnable !== undefined ? selectedItem.IsEnable : true,
        });
      }
    }
    setUpdateId(null);
  }, [updateId, itemList]);

  const handleSearchChange = (data) => {
    setSearchFields((prev) => ({
      ...prev,
      [data.name]: data.value,
    }));
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => ({
      ...prev,
      [data.name]: data.value,
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedSearch = {
      ...searchFields,
      SearchText: searchFields.SearchText?.trim() || "",
    };
    setSearchFields(trimmedSearch);
    // Reset to first page when searching
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(trimmedSearch, 1, pagination.pageSize);
  };

  const handleDeleteRow = (id) => {
    const payload = {
      OperationId: 4,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      SubCategoryId: id,
    };

    postRequest("/Inv_SubCategory", payload, controller)
      .then((response) => {
        if (response.error === true) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(response.data.DataSet?.Table?.errorMessage || "Error deleting sub category");
          return;
        }

        message.success(response.successMessage || "Sub category deleted successfully");
        fetchData(searchFields, pagination.current, pagination.pageSize);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error deleting sub category:", error);
          message.error("Failed to delete sub category");
        }
      });
  };

  const handleFormSubmit = (e, operationId) => {

    e.preventDefault();
    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      SubCategoryId: formFields.SubCategoryId,
      SubCategoryName: formFields.SubCategoryName,
      CategoryId: formFields.CategoryId,
      IsEnable: formFields.IsEnable,
    };

    postRequest("/Inv_SubCategory", payload, controller)
      .then((response) => {
        setFormLoading(false);

        const dataSet = response?.data;

        if (response.error === true) {
          message.error(response.errorMessage || response?.data?.Error_Message);
          return;
        }
        if (response?.data?.HasError === true) {
          message.error(response?.data?.Error_Message || "Error saving sub category");
          return;
        }

        message.success(response?.data?.Message || "Sub category saved successfully");

        setItemList(dataSet?.Data || []);

        // Set supporting tables if available (for categories dropdown)
        if (dataSet?.AdditionalData && Object.keys(dataSet.AdditionalData).length > 0) {
          setSupportingTable({ AdditionalData: dataSet.AdditionalData });
        }

        setFormFields(initialFormValues);
        setUpdateId(null);

        // Close modal if operationId is 3 (update) and response is successful
        if (operationId === 3) {
          setShouldCloseModal(true);
        }
      })
      .catch((error) => {
        setFormLoading(false);
        if (error.message !== "canceled") {
          console.error("Error saving sub category:", error);
          message.error("Failed to save sub category");
        }
      });
  };

  const handleFormClose = () => {
    setFormFields(initialFormValues);
    setUpdateId(null);
    setShouldCloseModal(false);
  };

  const handleReset = () => {
    setSearchFields(initialSearchValues);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(initialSearchValues, 1, pagination.pageSize);
  };

  const handlePagination = (paginationInfo, filters, sorter) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({
      ...prev,
      current: current,
      pageSize: pageSize,
    }));
    fetchData(searchFields, current, pageSize);
  };

  const searchPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Search"
        name="SearchText"
        size={INPUT_SIZE}
        value={searchFields.SearchText}
        onChange={handleSearchChange}
      />
    </Fragment>
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={8}
        label="Sub Category Name"
        name="SubCategoryName"
        size={INPUT_SIZE}
        value={formFields.SubCategoryName}
        onChange={handleFormChange}
        required={true}
      />
      <FormSelect
        colSpan={8}
        listItem={supportingTable?.AdditionalData?.Categories}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="CategoryId"
        label="Category"
        value={formFields.CategoryId}
        onChange={handleFormChange}
        required={true}
      />
      <div className="ant-col ant-col-8 mt-26">
        <FormCheckbox
          name="IsEnable"
          label="Is Enabled"
          checked={formFields.IsEnable}
          onChange={handleFormChange}
        />
      </div>
    </Fragment>
  );

  return (
    <BasicModalFormComponent
      formTitle="Inventory Sub Category"
      searchPanel={searchPanel}
      formPanel={formPanel}
      searchSubmit={handleSearchSubmit}
      onFormSave={handleFormSubmit}
      onFormClose={handleFormClose}
      tableRows={itemList}
      tableLoading={tableLoading}
      formLoading={formLoading}
      tableColumn={columns}
      deleteRow={handleDeleteRow}
      actionID="SubCategoryId"
      editRow={setUpdateId}
      fields={initialFormValues}
      reset={false}
      handleReset={handleReset}
      crudTitle="Sub Category"
      externalCloseTrigger={shouldCloseModal}
      onPaginationChange={handlePagination}
      paginationConfig={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      }}
    />
  );
};

export default InventorySubCategory;
